import "./UserDropdownMenu.scss";

import { map } from "lodash";
import type { PropsWithChildren } from "react";
import React, { memo, useEffect, useRef, useState } from "react";
import { Dropdown, OverlayTrigger } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import type { IOrganization } from "../../contexts/organizations/organizationsContext";
import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import { useUser } from "../../contexts/userContext";
import { CheckIcon } from "../icons";
import { ProfilePicture } from "../ProfilePicture/ProfilePicture";
import { renderTooltip } from "../StyledTooltip/StyledTooltip";

const UserDropdownMenu: React.FC = memo(() => {
  const { user } = useUser();
  const userName = user ? `${user.firstName[0]}${user.lastName[0]}` : "";

  const navigate = useNavigate();
  const logoutUpdated = async () => {
    navigate("/auth/logout");
  };

  const userDataContainer = useRef<HTMLDivElement>(null);
  const userEmail = useRef<HTMLLabelElement>(null);

  const [isEmailFit, setIsEmailFit] = useState<boolean>(true);

  const { organizations, currentOrganization, setCurrentOrganization } =
    useOrganizationContext();

  const selectOrganization = (org: IOrganization) => {
    setCurrentOrganization(org);
  };

  const handleToggle = (value: boolean) => {
    if (!value) return;
    setTimeout(() => {
      if (userDataContainer.current && userEmail.current) {
        if (
          userDataContainer.current.offsetWidth <
          userEmail.current.offsetWidth + 50
        ) {
          setIsEmailFit(false);
        } else {
          setIsEmailFit(true);
        }
      }
    });
  };

  return (
    <Dropdown onToggle={handleToggle}>
      <Dropdown.Toggle as="div" className="app-navbar-item no-arrow">
        <div className="cursor-pointer symbol app-logged-user-wrapper symbol-35px symbol-md-40px">
          <div className="app-logged-user-details d-none d-lg-block">
            <div className="app-logged-user-email text-truncate">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="app-logged-user-role text-truncate">
              {user?.email}
            </div>
          </div>
        </div>
        <ProfilePicture
          label={userName}
          firstName={user?.firstName}
          lastName={user?.lastName}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        renderOnMount={false}
        align="end"
        className="user-dropdown"
      >
        <div className="user-dropdown__user-data" ref={userDataContainer}>
          <div className="d-flex align-items-center symbol symbol-50px me-3">
            <ProfilePicture
              label={userName}
              firstName={user?.firstName}
              lastName={user?.lastName}
            />
          </div>

          <div
            className={`d-flex flex-column ${
              isEmailFit ? "" : "text-truncate"
            }`}
          >
            <div className="fs-5 fw-medium text-truncate">
              {user?.firstName} {user?.lastName}
            </div>
            {isEmailFit ? (
              <label ref={userEmail} className="fw-normal text-muted fs-7">
                {user?.email}
              </label>
            ) : (
              <OverlayTrigger
                placement="bottom"
                overlay={renderTooltip(
                  <span className="text-nowrap">{user?.email}</span>,
                  "user-dropdown__tooltip text-nowrap",
                )}
              >
                <label className="fw-normal text-muted fs-7 text-truncate">
                  {user?.email}
                </label>
              </OverlayTrigger>
            )}
          </div>
        </div>
        <div className="user-dropdown__separator" />
        <div className="user-dropdown__org-settings">
          <p className="user-dropdown__org-settings__header">
            Your organizations
          </p>
          <ul className="user-dropdown__org-settings__list">
            {map(organizations, org => {
              return (
                <li
                  className="d-flex align-items-center justify-content-between user-dropdown__org-settings__item"
                  key={org.id}
                  onClick={() => {
                    selectOrganization(org);
                  }}
                >
                  <span className="d-flex align-items-center ">
                    <ProfilePicture label={org.name} />
                    <span className="label">{org.name}</span>
                  </span>
                  {currentOrganization?.id === org.id && <CheckIcon />}
                </li>
              );
            })}
          </ul>
          <div className="user-dropdown__separator" />
        </div>
        <Dropdown.Item as="span">
          <Link to="/account/overview" className="dropdown-item">
            <span>My account</span>
          </Link>
        </Dropdown.Item>

        <Dropdown.Item onClick={logoutUpdated}>
          <span>Sign out</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
});

export { UserDropdownMenu };
